import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Contract, ContractStatus, WorkspaceType, FileReference } from "shared-types";
import { Button } from "../../../../Common/Components/BaseComponents/Button";
import { PageContainer } from "../../../../Common/Components/BaseComponents/PageContainer";
import { useContractStore } from "../../../../Stores/LeadAndCustomer/contractsStore";
import { Pencil, Trash, ExternalLink } from "lucide-react";
import { showAlert } from "../../../../Common/Components/BaseComponents/ShowAlert";
import { ShowAlertWarn } from "../../../../Common/Components/BaseComponents/showAlertWarn";
import { documentApi } from "../../../../services/api";

const statusLabels: Record<ContractStatus, string> = {
    DRAFT: "טיוטה",
    PENDING_SIGNATURE: "ממתין לחתימה",
    SIGNED: "חתום",
    ACTIVE: "פעיל",
    EXPIRED: "פג תוקף",
    TERMINATED: "הסתיים",
    RENEWED: "חודש"
};

const workspaceTypeLabels: Record<WorkspaceType, string> = {
    PRIVATE_ROOM1: "חדר פרטי",
    PRIVATE_ROOM2: "חדר של 2",
    PRIVATE_ROOM3: "חדר של 3",
    DESK_IN_ROOM: "שולחן בחדר",
    OPEN_SPACE: "עמדה במרחב פתוח",
    KLIKAH_CARD: "כרטיס קליקה",
    KLIKAH_CARD_UPGRADED: "כרטיס קליקה משודרג",
    DOOR_PASS: "כרטיס כניסה",
    WALL: "קיר",
    RECEPTION_DESK: "דלפק קבלה",
    COMPUTER_STAND: "עמדת מחשב",
    BASE: "בסיס"
};

const formatDate = (iso?: string) =>
    iso ? new Date(iso).toISOString().split("T")[0].split("-").reverse().join("/") : "";

export const ContractDetails = () => {
    const { customerId } = useParams<{ customerId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const customerName = location.state?.customerName ?? "לא ידוע";
    const { fetchContractsByCustomerId, loading, error } = useContractStore();
    const [customerContracts, setCustomerContracts] = useState<Contract[]>([]);
    const [openContractId, setOpenContractId] = useState<string | null>(null);
    const [contractDocuments, setContractDocuments] = useState<Record<string, FileReference[]>>({});

    const fetchDocumentDetails = async (contractId: string, documentIds: string[]) => {
        if (!documentIds || documentIds.length === 0) return;

        try {
            const documents = await Promise.all(
                documentIds.map(id => documentApi.getById(id).catch(() => null))
            );
            const validDocuments = documents.filter(Boolean) as FileReference[];

            setContractDocuments(prev => ({
                ...prev,
                [contractId]: validDocuments
            }));
        } catch (error) {
            console.error('Error fetching document details:', error);
        }
    };

    useEffect(() => {
        if (!customerId) return;
        fetchContractsByCustomerId(customerId)
            .then(() => {
                const results = useContractStore.getState().contracts.filter(c => c.customerId === customerId);
                setCustomerContracts(results);

                // Fetch document details for each contract
                results.forEach(contract => {
                    if (contract.documents && contract.documents.length > 0) {
                        fetchDocumentDetails(contract.id!, contract.documents);
                    }
                });
            })
            .catch(() => setCustomerContracts([]));
    }, [customerId, fetchContractsByCustomerId]);

    const toggleContract = (id: string) => {
        setOpenContractId(prev => (prev === id ? null : id));
    };


    const updateContract = (id: string, customerName: string) => {
        navigate(`/leadAndCustomer/contracts/edit/${id}`, {
            state: { customerName },
        })
    };

    const deleteContract = async (id: string) => {
        const confirmed = await ShowAlertWarn(
            "האם את בטוחה שברצונך למחוק חוזה זה?",
            "הפעולה אינה ניתנת לביטול"
        );
        if (confirmed) {
            await useContractStore
                .getState()
                .handleDeleteContract(id!)
                .then(() => {
                    showAlert("נמחק", "החוזה נמחק בהצלחה", "success");
                    setCustomerContracts((prev) =>
                        prev.filter((c) => c.id !== id)
                    );
                })
                .catch(() => {
                    showAlert("שגיאה", "אירעה שגיאה במחיקת החוזה", "error");
                });
        }
    };

    if (loading) return <p className="text-center text-gray-600">טוען חוזים...</p>;
    if (error) return <p className="text-red-600 text-center">{error}</p>;
    if (customerContracts.length === 0) return <p className="text-center">לא נמצאו חוזים להצגה.</p>;

    return (
        <PageContainer title={`חוזים של ${customerName}`}>
            <div dir="rtl">
                {customerContracts.map((contract, index) => {
                    const isOpen = contract.id === openContractId;
                    return (
                        <div key={contract.id} className="mb-6 border border-gray-200 rounded-xl shadow-md bg-white overflow-hidden transition">
                            <button
                                onClick={() => toggleContract(contract.id!)}
                                className="w-full text-right px-4 py-3 bg-blue-100 hover:bg-blue-200 text-blue-800 font-semibold flex items-center justify-between"
                            >
                                <span>חוזה #{index + 1} - {statusLabels[contract.status]}</span>
                                <span className="text-sm text-gray-600">({formatDate(contract.startDate)} - {formatDate(contract.endDate)})</span>
                            </button>

                            {isOpen && (
                                <div className="p-5 bg-gray-50 text-sm text-gray-800">
                                    <div className="flex justify-end gap-2 mb-4">
                                        <Button
                                            color="gray"
                                            size="sm"
                                            className="hover:ring hover:ring-blue-300 transition"
                                            onClick={() => updateContract(contract.id!, customerName)}
                                            title="עריכת חוזה"
                                        >
                                            <Pencil size={16} className="mr-1" />
                                            ערוך
                                        </Button>

                                        <Button
                                            color="red"
                                            size="sm"
                                            className="hover:ring hover:ring-red-300 transition"
                                            onClick={() => deleteContract(contract.id!)}
                                            title="מחיקת חוזה"
                                        >
                                            <Trash size={16} className="mr-1" />
                                            מחק
                                        </Button>
                                    </div>

                                    {/* Two column layout */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Left column - Contract details */}
                                        <div className="space-y-4">
                                            <div>
                                                <h4 className="font-semibold text-gray-700 mb-3 text-base">פרטי החוזה</h4>
                                                <div className="grid grid-cols-1 gap-y-3 bg-white p-4 rounded-lg border">
                                                    <div><strong>גרסה:</strong> {contract.version}</div>
                                                    {contract.signDate && <div><strong>תאריך חתימה:</strong> {formatDate(contract.signDate)}</div>}
                                                    <div><strong>תאריך התחלה:</strong> {formatDate(contract.startDate)}</div>
                                                    <div><strong>תאריך סיום:</strong> {formatDate(contract.endDate)}</div>
                                                    <div><strong>סטטוס:</strong> {statusLabels[contract.status]}</div>
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="font-semibold text-gray-700 mb-3 text-base">מקום ישיבה</h4>
                                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                                                    <div className="flex items-center mb-2">
                                                        <div className="w-3 h-3 bg-blue-500 rounded-full ml-2"></div>
                                                        <span className="font-semibold text-blue-800">
                            {contract.terms?.workspaceType !== undefined ? workspaceTypeLabels[contract.terms.workspaceType] : "לא נקבע"}
                          </span>
                                                    </div>
                                                    <div className="text-sm text-blue-700">
                                                        <strong>כמות עמדות:</strong> {contract.terms?.workspaceCount ?? "—"}
                                                    </div>
                                                    {contract.terms?.workspaceType && (
                                                        <div className="text-xs text-blue-600 mt-2">
                                                            סוג חלל העבודה: {contract.terms.workspaceType}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="font-semibold text-gray-700 mb-3 text-base">תנאי חוזה</h4>
                                                <div className="grid grid-cols-1 gap-y-2 bg-white p-4 rounded-lg border">
                                                    <div><strong>תעריף חודשי:</strong> {contract.terms?.monthlyRate ?? "—"} ₪</div>
                                                    <div><strong>משך חודשים:</strong> {contract.terms?.duration ?? "—"}</div>
                                                    <div><strong>תנאי חידוש:</strong> {contract.terms?.renewalTerms ?? "—"}</div>
                                                    <div><strong>הודעת סיום:</strong> {contract.terms?.terminationNotice ?? "—"} ימים</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right column - Documents */}
                                        <div className="space-y-4">

                                            <div>
                                                <h4 className="font-semibold text-gray-700 mb-3 text-base">מסמכים</h4>
                                                <div className="bg-white p-4 rounded-lg border max-h-80 overflow-y-auto">
                                                    {contractDocuments[contract.id!]?.length > 0 ? (
                                                        <div className="space-y-3">
                                                            {contractDocuments[contract.id!].map((doc: FileReference) => (
                                                                <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition">
                                                                    <div className="flex items-center space-x-3">
                                                                        <div className="flex-shrink-0">
                                                                            {doc.mimeType?.startsWith('image/') ? (
                                                                                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                                                                    <span className="text-green-600 text-xs font-bold">IMG</span>
                                                                                </div>
                                                                            ) : doc.mimeType === 'application/pdf' ? (
                                                                                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                                                                                    <span className="text-red-600 text-xs font-bold">PDF</span>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                                                                    <span className="text-gray-600 text-xs font-bold">DOC</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1">
                                                                            <p className="text-sm font-medium text-gray-900">{doc.name || `מסמך ${doc.id}`}</p>
                                                                            <p className="text-xs text-gray-500">
                                                                                {doc.size && `${Math.round(doc.size / 1024)} KB`}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center space-x-2">
                                                                        {doc.url && (
                                                                            <Button
                                                                                color="blue"
                                                                                size="sm"
                                                                                onClick={() => window.open(doc.url, '_blank')}
                                                                                className="flex items-center space-x-1"
                                                                                title="צפה במסמך"
                                                                            >
                                                                                <ExternalLink size={14} />
                                                                                <span>צפה</span>
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (contract.documents ?? []).length > 0 ? (
                                                        <div className="text-center py-8">
                                                            <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                                                            <p className="text-gray-500">טוען מסמכים...</p>
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-8">
                                                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                                                <span className="text-gray-400 text-xs">אין</span>
                                                            </div>
                                                            <p className="text-gray-500">לא נמצאו מסמכים</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="font-semibold text-gray-700 mb-3 text-base">פרטים נוספים</h4>
                                                <div className="text-gray-600 space-y-2 text-xs bg-white p-4 rounded-lg border">
                                                    {contract.signedBy && <div><strong>חתום על ידי:</strong> {contract.signedBy}</div>}
                                                    {contract.witnessedBy && <div><strong>עד/ה:</strong> {contract.witnessedBy}</div>}
                                                    <div><strong>נוצר:</strong> {formatDate(contract.createdAt)}</div>
                                                    <div><strong>עודכן:</strong> {formatDate(contract.updatedAt)}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </PageContainer>
    );
}